import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Wallet, ArrowLeft, CurrencyDollar, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { resolveMedia } from "@/lib/media";
import HlsVideo from "@/components/HlsVideo";

/**
 * PayPost — final step in the video publish flow. Loads a post in `pending_payment`
 * and shows the fee + "Pay from balance" button. On success the post moves to
 * `pending_approval` (admin review) and the user is bounced back to their profile.
 */
export default function PayPost() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [post, setPost] = useState(null);
  const [settings, setSettings] = useState(null);
  const [paying, setPaying] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/posts/${postId}/detail`),
      api.get("/settings"),
    ])
      .then(([p, s]) => {
        setPost(p.data);
        setSettings(s.data);
        if (p.data.status === "pending_approval" || p.data.status === "live") {
          toast.success("Already submitted");
          navigate(`/u/${user?.user_id}`, { replace: true });
        }
        if (p.data.status === "draft") {
          navigate(`/editor/${postId}`, { replace: true });
        }
      })
      .catch(() => {
        toast.error("Post not found");
        navigate("/post");
      })
      .finally(() => setLoading(false));
  }, [postId, navigate, user?.user_id]);

  const payFromBalance = async () => {
    setPaying(true);
    try {
      await api.post(`/posts/${postId}/pay-with-balance`);
      toast.success("Paid — awaiting admin approval");
      refresh?.();
      navigate(`/u/${user?.user_id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("Not enough Credits. Top up to publish.");
        setTimeout(() => navigate("/buy-points"), 800);
      } else {
        toast.error(e.response?.data?.detail || "Payment failed");
      }
    } finally {
      setPaying(false);
    }
  };

  if (loading) return <div className="p-8 text-white/60">Loading…</div>;
  if (!post) return <div className="p-8 text-white/60">Not found.</div>;

  const points = user?.points ?? 0;
  const cost = settings?.upload_point_cost ?? 10;
  const canPay = points >= cost;
  const fee = settings?.publish_fee ?? 0;

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-8 space-y-6" data-testid="paypost-page">
      <button
        data-testid="paypost-back"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-white/70 hover:text-white text-sm"
      >
        <ArrowLeft size={16} weight="bold" /> Back
      </button>

      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-black">Pay to publish</h1>
        <p className="text-white/60 mt-1">
          Once you pay, admin will review your post and publish it to the feed.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Preview */}
        <div className="aspect-[9/16] bg-black rounded-2xl overflow-hidden relative">
          {post.content_type === "video" && (post.hls_url || post.media_url) && (
            <HlsVideo
              src={resolveMedia(post.hls_url || post.media_url)}
              poster={post.thumbnail_url ? resolveMedia(post.thumbnail_url) : undefined}
              controls
              playsInline
              className="w-full h-full object-contain"
              data-testid="paypost-preview"
            />
          )}
        </div>

        {/* Fee card */}
        <div className="space-y-4">
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <CheckCircle size={22} weight="fill" className="text-emerald-400" />
                Ready to publish
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {post.caption && (
                <p className="text-sm text-white/70 whitespace-pre-line" data-testid="paypost-caption">
                  {post.caption}
                </p>
              )}

              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-xs uppercase tracking-widest font-bold text-blue-300">Publish fee</p>
                <p className="font-heading text-4xl font-black text-white mt-1" data-testid="paypost-fee">
                  {cost} Credit{cost === 1 ? "" : "s"}
                </p>
                <p className="text-xs text-white/50 mt-1">
                  Your balance:{" "}
                  <span className="font-bold text-emerald-400" data-testid="paypost-balance">
                    {points} cr
                  </span>
                  {" · "}${fee.toFixed(2)} equivalent
                </p>
              </div>

              <Button
                data-testid="paypost-pay-btn"
                onClick={payFromBalance}
                disabled={paying || !canPay}
                className={`w-full h-14 font-heading font-bold text-lg gap-2 ${
                  canPay ? "bg-emerald-600 hover:bg-emerald-700" : "bg-white/5 text-white/40"
                }`}
              >
                <Wallet size={22} weight="fill" />
                {paying ? "Processing…" : canPay ? `Pay ${cost} Credits & Publish` : `Need ${cost - points} more Credits`}
              </Button>

              {!canPay && (
                <Button
                  onClick={() => navigate("/buy-points")}
                  data-testid="paypost-topup-btn"
                  variant="outline"
                  className="w-full border-white/20 bg-transparent hover:bg-white/5 gap-2"
                >
                  <CurrencyDollar size={16} weight="bold" /> Buy Credits
                </Button>
              )}

              <Button
                onClick={() => navigate(`/editor/${postId}`)}
                data-testid="paypost-edit-btn"
                variant="ghost"
                className="w-full text-white/50 hover:text-white hover:bg-white/5"
              >
                ← Back to editor
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
